// New client verification: email + code from link, set password, then redirect to store.
// Link format: /store/newclientidentification?email=...&code=...

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { postWorkerApi } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { reportCaughtError } from '@/lib/report-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const NewClientIdentification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const e = searchParams.get('email') ?? '';
    const c = searchParams.get('code') ?? '';
    setEmail(decodeURIComponent(e));
    setCode(c);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email?.trim()) {
      toast({ title: 'Falta el email', variant: 'destructive' });
      return;
    }
    if (!code?.trim()) {
      toast({ title: 'Introduce el código que recibiste por correo', variant: 'destructive' });
      return;
    }
    if (password.length < 8) {
      toast({ title: 'La contraseña debe tener al menos 8 caracteres', variant: 'destructive' });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Las contraseñas no coinciden', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const result = await postWorkerApi<{ success: boolean }>('/api/verify-client', {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        password,
      });
      if (!result.ok) {
        toast({ title: 'Error', description: result.error ?? 'Código inválido o expirado', variant: 'destructive' });
        return;
      }
      // Pequeña espera para que Supabase Auth propague el usuario recién creado.
      await new Promise((r) => setTimeout(r, 500));
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });

      const pendingToken = localStorage.getItem('pendingReclaimToken');

      if (error) {
        // Limpiar estado de auth para evitar sesión corrupta en el login.
        await supabase.auth.signOut().catch(() => {});
        toast({ title: 'Cuenta creada', description: 'Inicia sesión con tu email y contraseña.', variant: 'default' });
        const reclaimRedirect = pendingToken
          ? `/store/catalog/${pendingToken}/reclaim`
          : '/store/quotations';
        // Carga completa para resetear todo el estado de la app (evita auth stale).
        window.location.href = `/store/login?redirect=${encodeURIComponent(reclaimRedirect)}`;
        return;
      }

      // Auto-login exitoso. Si hay reclaim pendiente, redirigir allí.
      if (pendingToken) {
        toast({ title: 'Cuenta verificada', description: 'Reclamando tu cotización…' });
        navigate(`/store/catalog/${pendingToken}/reclaim`, { replace: true });
        return;
      }

      toast({
        title: 'Bienvenido',
        description: 'Tu cuenta está verificada. Puedes ver el estado de tus cotizaciones en Mis cotizaciones.',
        variant: 'success',
      });
      navigate('/store/quotations', { replace: true });
    } catch (err) {
      reportCaughtError(err, { screen: 'newclientidentification', action: 'verify_client' });
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Error de conexión', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Completar verificación</CardTitle>
          <CardDescription>
            Introduce el código que te enviamos por correo y elige una contraseña para tu cuenta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="mt-1"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="code">Código de verificación</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Código de 6 dígitos"
                className="mt-1 font-mono"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña (mín. 8 caracteres)</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Completando...' : 'Verificar y crear cuenta'}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            ¿Ya tienes cuenta? <Link to="/store/login" className="text-primary underline">Iniciar sesión</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const CODE_LENGTH = 6;

export default NewClientIdentification;
